import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { lazy, Suspense, useState } from "react";
import { useRelatedJournalEntries } from "@/hooks/useAccounting";
import { usePermissions } from "@/hooks/usePermissions";
import { formatDate } from "@/utils/date-format";

/**
 * Lazy on purpose, and it must stay that way — this is NOT for code-splitting.
 *
 * JournalEntryDetail imports InvoiceDetail and PurchaseDetail so it can link through to its source,
 * and both of those render THIS component. A static import is therefore a cycle, and the kind that
 * resolves to undefined at render rather than failing loudly. Deferring to first render breaks it
 * at module-evaluation time.
 *
 * The bundler will report that this import cannot be split into its own chunk, because
 * JournalEntriesPanel imports the same module statically. That is expected and harmless: the point
 * here is the cycle, not the chunk.
 */
const JournalEntryDetail = lazy(() => import("@/components/accounting/JournalEntryDetail"));

const money = (v: string) =>
  Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const th = {
  fontSize: 11,
  letterSpacing: "0.6px",
  textTransform: "uppercase" as const,
  color: "var(--color-graphite)",
  fontWeight: 600,
  borderBottom: "1px solid var(--color-card-border)",
  whiteSpace: "nowrap" as const,
};

/** JournalEntryStatus — 0 draft, 1 posted, 2 needs review. */
const STATUS = {
  0: { label: "Draft", color: "default" as const },
  1: { label: "Posted", color: "success" as const },
  2: { label: "Needs review", color: "warning" as const },
};

/**
 * The journal entries raised for one invoice or credit purchase.
 *
 * Deliberately shows more than the entries sourced ON the document. A partial payment's deposit is
 * sourced on the RECEIPT, a refund on the refund row, a clawback on the credit lot — so a plain
 * source match would show the settlement alone and imply nothing else ever happened, when the
 * corrections are exactly what someone opening this section is looking for.
 *
 * GFG-only. These are GFG's books, not the client's transaction history, so the section is absent
 * rather than empty for anyone without finance access.
 */
export default function RelatedJournalEntries({
  sourceType,
  sourceId,
}: {
  sourceType: number;
  sourceId: number;
}) {
  const [openId, setOpenId] = useState<number | null>(null);
  const { can } = usePermissions();
  const isFinance = can("view", "FINANCE");
  const { data: entries = [], isLoading } = useRelatedJournalEntries(
    isFinance ? sourceType : null,
    sourceId,
  );

  if (!isFinance) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          fontWeight: 600,
          color: "var(--color-tan, #a98d6b)",
          mb: 1.5,
        }}
      >
        Journal Entries
      </Typography>

      {isLoading ? (
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>Loading…</Typography>
      ) : entries.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
          No journal entries yet.
        </Typography>
      ) : (
        <Box
          sx={{
            border: "1px solid var(--color-card-border)",
            borderRadius: "var(--radius-card)",
            overflowX: "auto",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={th}>Journal No</TableCell>
                <TableCell sx={th}>Date</TableCell>
                <TableCell sx={th}>Event</TableCell>
                <TableCell sx={{ ...th, textAlign: "right" }}>Amount</TableCell>
                <TableCell sx={th}>Status</TableCell>
                <TableCell sx={th}>By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e) => {
                const s = STATUS[e.status as keyof typeof STATUS] ?? STATUS[0];
                return (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setOpenId(e.id)}
                        sx={{
                          appearance: "none",
                          background: "none",
                          border: "none",
                          p: 0,
                          cursor: "pointer",
                          fontFamily: "monospace",
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: "var(--color-rodeo-black)",
                          textDecoration: "underline",
                          textUnderlineOffset: 3,
                        }}
                      >
                        {e.journalNo}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{formatDate(e.accountingDate)}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {e.reasonLabel}
                      {e.memo && (
                        <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)" }}>
                          {e.memo}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{ textAlign: "right", fontSize: 13, fontVariantNumeric: "tabular-nums" }}
                    >
                      {money(e.totalDebit)}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={s.label} color={s.color} variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
                      {/* No actor means a webhook or a cron raised it, which is most of them. */}
                      {e.actorName ?? "System"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Opened over the invoice or purchase rather than navigating away — the entry is being
          checked against the document, so losing the document defeats the point.

          Lifted one above the modal layer because this component is not always hosted by a page:
          the adjustment audit view is a Dialog, and at MUI's shared default z-index the host wins
          and the drawer opens BEHIND it. Harmless where the host is a page, since nothing else
          competes for that layer there. */}
      <Drawer
        anchor="right"
        open={openId != null}
        onClose={() => setOpenId(null)}
        sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
        slotProps={{ paper: { sx: { width: { xs: "100%", md: 860 }, p: { xs: 2, md: 3 } } } }}
      >
        {openId != null && (
          <Suspense fallback={<CircularProgress aria-label="Loading" size={24} />}>
            <JournalEntryDetail
              journalEntryId={openId}
              onBack={() => setOpenId(null)}
              backLabel="Close"
            />
          </Suspense>
        )}
      </Drawer>
    </Box>
  );
}
